/* eslint-disable no-nested-ternary */
import * as React from "react";
import classNames from "classnames";
import PropTypes from "prop-types";

import { ButtonTileGroup } from "@lululemon/ecom-pattern-library-button-tile-group";
import { ButtonTile } from "@lululemon/ecom-pattern-library-button-tile";
import { Image } from "@lululemon/ecom-pattern-library-image";
import { Icon } from "@lululemon/ecom-pattern-library-icon";
import { linealTagIcon } from "@lululemon/ecom-pattern-library-icons";

import styles from "../styles/thumbnails.module.scss";
import ImageError from "./image-error";

const IMAGE_RESOLUTION_WIDTH = 72;
const NAME_PREFIX = "thumbnails_";

export function ThumbnailImage({ lazy, imageSrc, imageAlt }) {
  const [hasError, setError] = React.useState(false);
  return hasError ? (
    <ImageError isThumb className={styles.thumbnails__image} />
  ) : (
    <Image
      maxWidth={IMAGE_RESOLUTION_WIDTH}
      lazy={lazy}
      src={imageSrc}
      className={styles.thumbnails__image}
      alt={imageAlt}
      onError={() => setError(true)}
    />
  );
}

const Thumbnails = ({
  productPhotos,
  slideToIndex,
  imageAlt,
  activeSlide,
  showPdpModelIsWearing,
  onThumbnailHover,
  lazy,
  containerAriaLabel,
  buttonTrackKey,
  classes,
  containerTestId
}) => {
  return (
    productPhotos.length && (
      <ButtonTileGroup
        selectedIndex={activeSlide}
        onSelectCallback={(index) => {
          slideToIndex(index);
        }}
        className={classNames(styles.thumbnails, classes.container)}
        eventAttribute="onClick"
        wrapperComponent={(childrenArg) => (
          <div className={styles.thumbnailButtonTileGroupWrapper}>
            {childrenArg}
          </div>
        )}
        aria-label={containerAriaLabel}
        data-testid={containerTestId}
      >
        {productPhotos.map((imageSrc, index) => {
          const isActive = index === activeSlide;
          return (
            // eslint-disable-next-line jsx-a11y/mouse-events-have-key-events
            <ButtonTile
              onMouseOver={() => {
                if (onThumbnailHover) {
                  onThumbnailHover(index);
                }
              }}
              // eslint-disable-next-line react/no-array-index-key
              key={`${NAME_PREFIX}${imageSrc}${index}`}
              className={classNames(
                styles.thumbnails__button,
                {
                  [styles["thumbnails__button--active"]]: isActive
                },
                classes.button
              )}
              aria-current={isActive}
              data-lulu-track={
                buttonTrackKey ? `${buttonTrackKey}-${index}` : null
              }
            >
              {showPdpModelIsWearing && index === productPhotos.length - 1 ? (
                <Icon
                  className={styles.thumbnails__icon}
                  content={linealTagIcon}
                />
              ) : (
                <ThumbnailImage
                  lazy={lazy}
                  imageSrc={imageSrc}
                  imageAlt={`${imageAlt} button select list item`}
                />
              )}
            </ButtonTile>
          );
        })}
      </ButtonTileGroup>
    )
  );
};

Thumbnails.defaultProps = {
  showPdpModelIsWearing: false,
  onThumbnailHover: () => {},
  lazy: true,
  containerAriaLabel: null,
  buttonTrackKey: "",
  classes: {
    container: "",
    button: ""
  },
  containerTestId: null
};

Thumbnails.propTypes = {
  productPhotos: PropTypes.arrayOf(PropTypes.string).isRequired,
  slideToIndex: PropTypes.func.isRequired,
  imageAlt: PropTypes.string.isRequired,
  activeSlide: PropTypes.number.isRequired,
  showPdpModelIsWearing: PropTypes.bool,
  onThumbnailHover: PropTypes.func,
  lazy: PropTypes.bool,
  containerAriaLabel: PropTypes.string,
  buttonTrackKey: PropTypes.string,
  classes: PropTypes.shape({
    container: PropTypes.string,
    button: PropTypes.string
  }),
  containerTestId: PropTypes.string
};

export default Thumbnails;
